import React, { useState } from 'react';
import { AttachmentInfo, LanguageData } from '../../../types/chat';
import { Alert, Box, Chip, Collapse, IconButton, Stack, SxProps, Tooltip, Typography } from '@mui/material';
import { getPipelineDate } from '../../../utils/api';
import { ChatTheme, getItemColour, leftArrowSx, rightArrowSx } from '../themes';
import { getCurrentUrl, getFileType } from '../../../utils/string';
import { Info, Link, Translate } from '@mui/icons-material';
import { SnackbarAlert } from '../../SnackbarAlert';

type TimestampMethod = 'timestamp' | 'timestamp_from_start' | 'timestamp_from_last';

interface MessageContainerProps {
	linkStub: string;
	chatTheme: ChatTheme;
	userTheme: 'default' | 'light';
	userIsSender: boolean;
	senderId: string;
	messageId: string;
	messageTime: number;
	messageText: string;
	attachments?: AttachmentInfo[];
	timestampMethod?: TimestampMethod;
	deleted?: boolean;
	starred?: boolean;
	messageStatus?: string;
	systemMessage?: boolean;
	anonymous?: boolean;
	linkedMessage?: boolean;
	languageData?: LanguageData;
}

const MessageContainer: React.FC<MessageContainerProps> = ({
	linkStub,
	chatTheme,
	userTheme = 'default',
	userIsSender,
	senderId,
	messageId,
	messageTime,
	messageText,
	attachments = [],
	timestampMethod = 'timestamp',
	deleted = false,
	starred = false,
	messageStatus = '',
	systemMessage = false,
	anonymous = false,
	linkedMessage = false,
	languageData,
}) => {
	const messageLink = `${linkStub}?messageId=${messageId}`;
	const [linkCopied, setLinkCopied] = useState<boolean>(false);
	const [translationOpen, setTranslationOpen] = useState<boolean>(false);

	const getTimestampText = (messageTime: number, timestampMethod: TimestampMethod): string => {
		let timestampText = `Sent: ${getPipelineDate(messageTime)}`;
		if (timestampMethod === 'timestamp_from_start') {
			timestampText += '(timestamp derived from thread start time)';
		} else if (timestampMethod === 'timestamp_from_last') {
			timestampText += '(timestamp derived from thread last activity)';
		}

		return timestampText;
	};

	const adornmentSx: SxProps = {
		margin: 'auto',
		width: '100%',
		textAlign: 'center',
		position: 'relative',
		borderRadius: '10px',
	};

	const senderSx: SxProps = {
		backgroundColor: getItemColour('sent', chatTheme, userTheme),
		justifySelf: 'right',
		marginLeft: '20%',
		paddingBottom: '5px',
		marginBottom: '10px',
		borderRadius: '10px 0px 10px 10px',
		'::after': {
			...rightArrowSx,
			backgroundColor: getItemColour('sent', chatTheme, userTheme),
		},
		'@media print': {
			width: '80%',
		},
	};

	const receiverSx: SxProps = {
		backgroundColor: getItemColour('recv', chatTheme, userTheme),
		justifySelf: 'left',
		marginRight: '20%',
		paddingBottom: '5px',
		marginBottom: '10px',
		borderRadius: '0 10px 10px 10px',
		'::after': {
			...leftArrowSx,
			backgroundColor: getItemColour('recv', chatTheme, userTheme),
		},
		'@media print': {
			width: '80%',
		},
	};

	const systemSx: SxProps = {
		backgroundColor: getItemColour('system', chatTheme, userTheme),
		width: '100%',
		textAlign: 'center',
		marginY: '10px',
		padding: '5px',
	};

	let containerSx: SxProps = {};

	if (userIsSender) {
		containerSx = {
			...containerSx,
			...senderSx,
		};
	} else if (systemMessage) {
		containerSx = {
			...containerSx,
			...systemSx,
		};
	} else {
		containerSx = {
			...containerSx,
			...receiverSx,
		};
	}

	if (linkedMessage) {
		containerSx = {
			...containerSx,
			border: 4,
			borderColor: 'primary.main',
			filter: 'brightness(0.8)',
		};
	}

	return (
		<>
			<Box sx={containerSx} position={'relative'} padding={1} id={`message-${messageId}`} data-testid={`message-container-${messageId}`}>
				{deleted && (
					<Typography sx={{ ...adornmentSx, backgroundColor: getItemColour('del', chatTheme, userTheme) }}>
						Message was deleted 🚫
					</Typography>
				)}
				{starred && (
					<Typography sx={{ ...adornmentSx, backgroundColor: getItemColour('star', chatTheme, userTheme) }}>Message starred ⭐</Typography>
				)}
				{anonymous && (
					<Typography sx={{ ...adornmentSx, backgroundColor: getItemColour('anon', chatTheme, userTheme) }}>
						Message is anonymous 👤
					</Typography>
				)}

				{!systemMessage && (
					<Typography fontWeight={'bold'} width="100%" display={'flex'} justifyContent={'space-between'}>
						{`${senderId}:`}
						{languageData?.topLanguage && (
							<Tooltip title={`${translationOpen ? 'Hide' : 'View'} Translation`}>
								<Chip
									label={languageData.topLanguage}
									icon={<Translate />}
									onClick={() => setTranslationOpen(!translationOpen)}
									sx={{ '@media print': { pointerEvents: 'none' } }}
								/>
							</Tooltip>
						)}
					</Typography>
				)}

				<Box padding={2}>
					{attachments.length > 0 && <Typography>Message contained attachment(s):</Typography>}

					{attachments.map((attachment) => {
						return (
							<Box
								width="100%"
								margin="auto"
								bgcolor={getItemColour('system', chatTheme, userTheme)}
								padding={1}
								marginBottom={1}
								textAlign={'center'}
								key={`attachment-${attachment.filePath}`}
								borderRadius={1}
							>
								<Typography>{`File Path: ${attachment.filePath}`}</Typography>
								<Typography>{`File Name: ${attachment.fileName}`}</Typography>
								<Typography>{`Type: ${attachment.fileType} ${attachment.fileType === 'Unknown' ? '' : `(${getFileType(attachment.fileType)})`}`}</Typography>
							</Box>
						);
					})}

					<Typography data-testid={`${userIsSender ? 'sent' : systemMessage ? 'system' : 'received'}-text`}>{messageText}</Typography>

					<Collapse in={translationOpen} sx={{ paddingTop: 2 }}>
						{languageData?.errorLog && languageData.errorLog !== 'None' && (
							<Alert severity="error" variant="filled" sx={{ marginBottom: 2, '@media print': { display: 'none' } }}>
								Error occurred during translation: {languageData?.errorLog}
							</Alert>
						)}
						<Typography>
							<b>Most Likely Language:</b> {languageData?.topLanguage}{' '}
							<Tooltip title={`Identified Languages: ${languageData?.guessedLanguages.join(', ')}`}>
								<Info fontSize="inherit" />
							</Tooltip>
						</Typography>
						<Typography>
							<b>Attempted Translation:</b> {languageData?.translation}{' '}
							<Tooltip title={`Reasoning: ${languageData?.reasoning}`}>
								<Info fontSize="inherit" />
							</Tooltip>
						</Typography>
					</Collapse>
				</Box>

				<Stack width={'100%'} gap={4} textAlign={'left'} direction={'row'} alignContent={'center'} justifyContent={'space-between'}>
					<Box>
						<Typography>{messageTime != 0 ? getTimestampText(messageTime, timestampMethod) : 'Timestamp not found'}</Typography>

						{messageStatus && <Typography>{`${messageStatus}`}</Typography>}
					</Box>

					{/* Future site of message action buttons */}
					<Stack spacing={1} direction={'row'}>
						<Tooltip title="Copy Link to Message">
							<IconButton
								sx={{ '@media print': { display: 'none' } }}
								onClick={() => {
									navigator.clipboard.writeText(encodeURI(`${getCurrentUrl()}${messageLink}`));
									setLinkCopied(true);
								}}
							>
								<Link sx={{ transform: 'rotate(45deg)' }} />
							</IconButton>
						</Tooltip>
					</Stack>
				</Stack>
			</Box>

			<SnackbarAlert
				severity="success"
				open={linkCopied}
				onClose={() => setLinkCopied(false)}
				alertMessage={'Copied link to message'}
				testId="link-copied-snackbar"
			/>
		</>
	);
};

export default MessageContainer;
